import styled from "styled-components";
import { SubgridBodyColumn } from "../store/types";
import { Grid, Flex } from "src/elements";
import { SubgridRow } from "./SubgridRow";
import React, { RefObject, useEffect, useState, memo } from "react";

const VIRTUAL_ROW_BUFFER = 10;

const RowRendererWrapper = <T,>({
  renderer,
  data,
}: {
  renderer: (row: T) => React.ReactNode;
  data: T;
}) => {
  return <>{renderer(data)}</>;
};

const Body = styled(Grid)`
  grid-column: 1 / -1;
  grid-template-columns: subgrid;

  > * {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
  }
`;

interface Props<T> {
  bodyColumns: SubgridBodyColumn<T>[];
  data: T[];
  align?: "center";
  virtualRowHeight?: number;
  rowRenderer?: (row: T) => React.ReactNode;
  rowBackground?: (row: T) => string | undefined;
  refScroller?: RefObject<HTMLDivElement | null>;
}

const SubgridBodyInner = <T extends object>({
  bodyColumns,
  data,
  align,
  virtualRowHeight,
  rowRenderer,
  rowBackground,
  refScroller,
}: Props<T>) => {
  const [visibleRange, setVisibleRange] = useState<{ start: number; end: number } | null>(null);

  useEffect(() => {
    if (!refScroller?.current || !virtualRowHeight) return;

    const scroller = refScroller.current;

    const handleScroll = () => {
      const start = Math.floor(scroller.scrollTop / virtualRowHeight);
      const end = Math.ceil((scroller.scrollTop + scroller.clientHeight) / virtualRowHeight);

      const newRange = {
        start: Math.max(0, start - VIRTUAL_ROW_BUFFER),
        end: Math.min(data.length, end + VIRTUAL_ROW_BUFFER),
      };

      setVisibleRange((oldRange) => {
        if (oldRange?.start === newRange.start && oldRange?.end === newRange.end) return oldRange;
        return newRange;
      });
    };

    scroller.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => scroller.removeEventListener("scroll", handleScroll);
  }, [refScroller, data.length, virtualRowHeight]);

  const start = visibleRange?.start ?? 0;
  const end = visibleRange?.end ?? data.length;

  return (
    <Body>
      <React.Fragment>
        {virtualRowHeight && <Flex height={`${start * virtualRowHeight}px`} />}
      </React.Fragment>

      {data.slice(start, end).map((dataRow, rowIndex) => {
        const globalIndex = start + rowIndex;

        if (rowRenderer) {
          return <RowRendererWrapper key={globalIndex} renderer={rowRenderer} data={dataRow} />;
        }

        return (
          <SubgridRow
            key={globalIndex}
            row={dataRow}
            bodyColumns={bodyColumns}
            rowIndex={globalIndex}
            align={align}
            background={rowBackground ? rowBackground(dataRow) : undefined}
            height={virtualRowHeight ? `${virtualRowHeight}px` : undefined}
          />
        );
      })}

      <React.Fragment>
        {virtualRowHeight && <Flex height={`${(data.length - end) * virtualRowHeight}px`} />}
      </React.Fragment>
    </Body>
  );
};

export const SubgridBody = memo(SubgridBodyInner) as typeof SubgridBodyInner;
